/**
 * @genesis_domain Settings — Preferences ViewModel
 * @genesis_version V9.1
 * @genesis_intent ViewModel orchestrating preferences query and mutation hooks
 *   into clean state for consumption by UI components. Returns isLoading,
 *   preferences, and an updatePreference handler.
 * @genesis_boundaries FORBIDDEN from containing raw TanStack Query setup,
 *   UI rendering, or raw fetch() calls. Pure state orchestration only.
 *   FORBIDDEN from using Logical OR (||) for fallbacks — Nullish Coalescing (??) only.
 * @genesis_ai_trail Phase 3 Hybrid Sync Preferences. Follows use[Feature]ViewModel
 *   naming registry. Orchestrates usePreferencesQuery and usePreferencesMutation
 *   hooks per the Thin Hub Mandate (Rule 3.1).
 */

import { useCallback } from 'react';

import { usePreferencesMutation } from '@/domains/settings/hooks/usePreferencesMutation';
import { usePreferencesQuery } from '@/domains/settings/hooks/usePreferencesQuery';
import type { UpdatePreferences } from '@shared/schemas.js';

export function usePreferencesViewModel() {
    const { data: profile, isLoading } = usePreferencesQuery();
    const mutation = usePreferencesMutation();

    const preferences = profile?.preferences ?? {};

    const defaultExportFormat = preferences.export?.defaultFormat ?? 'json-ld';
    const includeImages = preferences.export?.includeImages ?? true;
    const includeNutrition = preferences.export?.includeNutrition ?? true;
    const includeDescription = preferences.export?.includeDescription ?? true;
    const includeMeta = preferences.export?.includeMeta ?? true;
    const includeIngredients = preferences.export?.includeIngredients ?? true;
    const includeInstructions = preferences.export?.includeInstructions ?? true;
    const includeSource = preferences.export?.includeSource ?? true;
    const globalTheme = preferences.appearance?.theme ?? 'Default Glass';
    const measurementSystem = preferences.measurements?.units ?? 'us';

    const updatePreference = useCallback(
        (payload: UpdatePreferences) => {
            mutation.mutate(payload);
        },
        [mutation],
    );

    return {
        isLoading,
        preferences,
        defaultExportFormat,
        includeImages,
        includeNutrition,
        includeDescription,
        includeMeta,
        includeIngredients,
        includeInstructions,
        includeSource,
        globalTheme,
        measurementSystem,
        updatePreference,
    };
}
